import { Preferences } from '@capacitor/preferences'

import { useIsNative } from './usePlatform'

// One record, so a write is atomic. Preferences has no transaction, and two keys cannot be
// written together, which used to leave a new access token beside an old refresh token.
const TOKEN_PAIR_KEY = 'auth.token_pair'

type TokenPair = { accessToken: string; refreshToken: string }

export const useCapacitorAuth = () => {
  const isNative = useIsNative()

  const setTokens = async (accessToken: string, refreshToken: string) => {
    if (!isNative) return

    const pair: TokenPair = { accessToken, refreshToken }

    await Preferences.set({
      key: TOKEN_PAIR_KEY,
      value: JSON.stringify(pair),
    })
  }

  const getTokens = async (): Promise<TokenPair | null> => {
    if (!isNative) return null

    const stored = await Preferences.get({ key: TOKEN_PAIR_KEY })
    if (!stored.value) return null

    try {
      return JSON.parse(stored.value) as TokenPair
    } catch (e) {
      console.error('Discarding an unreadable stored token pair:', e)
      await Preferences.remove({ key: TOKEN_PAIR_KEY })
      return null
    }
  }

  const clearTokens = async () => {
    if (!isNative) return

    await Preferences.remove({ key: TOKEN_PAIR_KEY })
  }

  // The API rotates the refresh token on every exchange, so the stored copy has to follow
  // the auth module's state. Writing it only at login leaves a dead token behind, and the
  // next cold start restores that one.
  const syncTokens = async () => {
    if (!isNative) return

    const auth = useAuth()
    const accessToken = auth.strategy.token?.get()
    const refreshToken = auth.strategy.refreshToken?.get()

    try {
      if (typeof accessToken === 'string' && typeof refreshToken === 'string') {
        await setTokens(accessToken, refreshToken)
        return
      }

      await clearTokens()
    } catch (e) {
      // Never reject: the callers are storage watchers, which cannot await. The record is
      // written whole, so a failure leaves the previous pair rather than a partial one.
      console.error('Failed to sync auth tokens:', e)
    }
  }

  return {
    setTokens,
    getTokens,
    clearTokens,
    syncTokens,
  }
}
